<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue';
import { useForm } from 'vee-validate';

import { Gender, genderOptions } from '@/helpers/const';
import { capitalise } from '@/helpers/format';
import { noWhiteSpaceKeys } from '@/helpers/noWhiteSpaceKeys';
import { useErhlLoanAppProcess } from '@/helpers/useErhlLoanAppProcess';
import { NEW_APPLICANT_ID, useExistingApplicantSelector } from '@/helpers/useExistingApplicantSelector';
import { useRelationshipManager } from '@/helpers/useRelationshipManager';
import { erhlApplicantSchema } from '@/validation';

import type { ApplicantEntry, ApplicantTypeEnum } from '@/types/Applicant';

const props = withDefaults(
  defineProps<{
    type: ApplicantTypeEnum;
    modelValue: ApplicantEntry;
    primary?: ApplicantEntry;
    allIndividuals?: ApplicantEntry[];
    currentId?: string;
    forceShowErrors?: boolean;
    excludedApplicantIds?: string[];
  }>(),
  {
    primary: undefined,
    allIndividuals: () => [],
    currentId: '',
    forceShowErrors: false,
    excludedApplicantIds: () => [],
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: ApplicantEntry): void;
  (e: 'relationship-change', individualId: string, newStatus: string, oldStatus?: string): void;
  (e: 'sync-applicant', applicant: ApplicantEntry): void;
}>();

const isLinkedApplicant = (id: string) => props.allIndividuals.some((a) => a.id === id && a.firstName && a.lastName);

const computeSelectionId = (individual: ApplicantEntry): string | undefined => {
  if (!individual.firstName || !individual.lastName) return undefined;
  return isLinkedApplicant(individual.id) ? individual.id : NEW_APPLICANT_ID;
};

const allIndividualsRef = toRef(props, 'allIndividuals');
const excludedIdsRef = toRef(props, 'excludedApplicantIds');
const individualsRef = ref<ApplicantEntry[]>(props.allIndividuals || []);
const selectedExistingId = ref<string | undefined>(computeSelectionId(props.modelValue));

// Track if current record is saved (has firstName and lastName)
const isSavedRecord = computed(() => Boolean(props.modelValue.firstName && props.modelValue.lastName));

const { loanPropertyAddresses } = useErhlLoanAppProcess();
const { dropdownOptions, findApplicantById, isNewApplicant } = useExistingApplicantSelector(
  allIndividualsRef,
  excludedIdsRef,
  selectedExistingId,
  isSavedRecord,
);
const { getRelationshipOptions, validate } = useRelationshipManager(individualsRef);

// Check if there are existing applicants to select (besides "+ Add new applicant")
const hasExistingApplicants = computed(() =>
  Object.keys(dropdownOptions.value).some((key) => key !== NEW_APPLICANT_ID),
);

// Show form fields when: user selects from dropdown OR no dropdown is shown
const showFormFields = computed(() => selectedExistingId.value !== undefined || !hasExistingApplicants.value);

const schema = computed(() => erhlApplicantSchema(props.primary, props.allIndividuals, props.currentId));
const { meta, submitCount, values, handleSubmit, resetForm, setFieldValue } = useForm<ApplicantEntry>({
  validationSchema: schema,
  validateOnMount: false,
  initialValues: { ...props.modelValue, type: props.type },
});

const relationshipOptions = computed(() => {
  return getRelationshipOptions(props.currentId);
});

const relationshipErrors = computed(() => {
  const allErrors = validate.value;
  const id = props.currentId;
  if (!id) return [];
  return allErrors.filter((error) => error.includes(`[${id}]`));
});

const shouldShowRelationshipErrors = computed(() => {
  return relationshipErrors.value.length > 0 && (submitCount.value > 0 || meta.value.touched || props.forceShowErrors);
});

// Format the relationship error message by stripping the [id] prefix and deduplicating
const formattedRelationshipError = computed(() => {
  if (relationshipErrors.value.length === 0) return '';
  const messages = relationshipErrors.value.map((error) => error.replace(/^\[[^\]]+\]\s*/, ''));
  return [...new Set(messages)].join('; ');
});

const updatePreferredName = () => {
  values.firstName = capitalise(values.firstName);
  if (values.firstName && !values.preferredName) {
    setFieldValue('preferredName', values.firstName);
  }
};

const onlyValidPhoneKeys = (event: KeyboardEvent) => {
  if ((event.key < '0' || event.key > '9') && event.key !== '+') {
    event.preventDefault();
  }
};

const handleApplicantSelection = (value: string | Record<string, unknown>) => {
  const newId = typeof value === 'string' ? value : (value.id as string);
  selectedExistingId.value = newId;

  if (isNewApplicant(newId)) {
    // User selected "+ Add new applicant" - reset to empty form
    resetForm({ values: { id: props.modelValue.id, type: props.type } });
    return;
  }

  // User selected an existing applicant - pre-fill with their data
  const existingApplicant = findApplicantById(newId);
  if (existingApplicant) {
    // Copy all data including the existing applicant's ID (important for deduplication)
    const preFillData: ApplicantEntry = {
      ...existingApplicant,
      type: props.type,
      confirmEmail: existingApplicant.email,
    };
    resetForm({ values: preFillData });
    emit('update:modelValue', preFillData);
  }
};

const update = handleSubmit(
  (values) => {
    if (values.relationshipStatus !== props.modelValue.relationshipStatus) {
      emit(
        'relationship-change',
        props.currentId,
        values.relationshipStatus || '',
        props.modelValue.relationshipStatus || '',
      );
    }

    emit('update:modelValue', values);

    // Always emit sync to update any other references to this applicant
    emit('sync-applicant', values);

    return true;
  },
  () => false,
);

const cancel = () => resetForm();

watch(
  () => props.modelValue,
  (individual) => {
    resetForm({ values: { ...individual, type: props.type } });

    // Update dropdown selection when modelValue has saved data
    const newSelectionId = computeSelectionId(individual);
    if (newSelectionId !== undefined) {
      selectedExistingId.value = newSelectionId;
    }
  },
  { immediate: true, deep: true },
);

watch(
  () => props.allIndividuals,
  (newIndividuals) => {
    individualsRef.value = newIndividuals || [];
  },
  { immediate: true, deep: true },
);

watch(
  () => individualsRef.value,
  (newIndividuals) => {
    const currentIndividual = newIndividuals.find((a) => a.id === props.currentId);
    if (currentIndividual && currentIndividual.relationshipStatus !== values.relationshipStatus) {
      setFieldValue('relationshipStatus', currentIndividual.relationshipStatus);
    }
  },
  { deep: true },
);

defineExpose({ update, cancel, resetForm, setFieldValue });
</script>

<template>
  <form>
    <div>
      <div v-if="hasExistingApplicants" class="form-group mb-4 mt-4">
        <BxSelect
          :id="values.id + '-existing-applicant'"
          :data-test-id="values.id + '-existing-applicant'"
          :model-value="selectedExistingId"
          :list-items="dropdownOptions"
          label="Select existing applicant"
          label-type="floating"
          class="form-control !mt-0"
          @update:model-value="handleApplicantSelection"
        />
      </div>

      <template v-if="showFormFields">
        <div class="form-group mt-4">
          <BxField v-slot="{ field }" name="firstName">
            <BxInput
              :id="values.id + '-first-name'"
              :data-test-id="values.id + '-first-name'"
              class="form-control"
              v-bind="field"
              label="First name"
              label-type="floating"
              @blur="updatePreferredName()"
            />
          </BxField>
          <BxField v-slot="{ field }" name="middleName">
            <BxInput
              :id="values.id + '-middle-name'"
              :data-test-id="values.id + '-middle-name'"
              class="form-control"
              v-bind="field"
              label="Middle name"
              label-type="floating"
              @blur="values.middleName = capitalise(values.middleName)"
            />
          </BxField>
          <BxField v-slot="{ field }" name="lastName">
            <BxInput
              :id="values.id + '-last-name'"
              :data-test-id="values.id + '-last-name'"
              class="form-control"
              v-bind="field"
              label="Last name"
              label-type="floating"
              @blur="values.lastName = capitalise(values.lastName)"
            />
          </BxField>
          <BxField v-slot="{ field }" name="preferredName">
            <BxInput
              :id="values.id + '-preferred-name'"
              :data-test-id="values.id + '-preferred-name'"
              class="form-control"
              v-bind="field"
              label="Preferred name"
              label-type="floating"
            />
          </BxField>
          <BxField v-slot="{ field }" name="relationshipStatus">
            <BxSelect
              :id="values.id + '-relationship-status'"
              :data-test-id="values.id + '-relationship-status'"
              class="form-control"
              v-bind="field"
              :list-items="relationshipOptions"
              label="Relationship"
              label-type="floating"
              :error-message="shouldShowRelationshipErrors ? formattedRelationshipError : field.errorMessage"
            />
          </BxField>
        </div>
        <div class="form-control mt-4">
          <BxField v-slot="{ field }" name="gender">
            <BxSelect
              :id="values.id + '-gender'"
              :data-test-id="values.id + '-gender'"
              v-bind="field"
              :list-items="genderOptions"
              label="Gender"
              label-type="floating"
              block
            />
          </BxField>
          <Transition name="fade">
            <div v-show="values.gender === Gender.OTHER">
              <BxField v-slot="{ field }" name="genderOther">
                <BxInput
                  :data-test-id="values.id + '-gender-other'"
                  class="mt-3"
                  v-bind="field"
                  label=""
                  aria-label="Gender identity"
                  placeholder="Describe your gender identity"
                />
              </BxField>
            </div>
          </Transition>
        </div>
        <BxField v-slot="{ field }" name="birthdate">
          <BxDateInput
            :id="values.id + '-date-of-birth'"
            :data-test-id="values.id + '-date-of-birth'"
            class="form-control mt-5"
            v-bind="field"
            label="Date of birth"
            label-type="floating"
            placeholder="DD/MM/YYYY"
          />
        </BxField>
        <BxField v-slot="{ field }" name="address">
          <BxAddressInput
            :id="values.id + '-address'"
            :data-test-id="values.id + '-address'"
            class="form-control"
            v-bind="field"
            label="Residential address"
            label-type="floating"
            :suggested-addresses="loanPropertyAddresses"
          />
        </BxField>
        <BxField v-slot="{ field }" name="mobile">
          <BxInput
            :id="values.id + '-mobile-phone-number'"
            :data-test-id="values.id + '-mobile-phone-number'"
            class="form-control"
            inputmode="tel"
            v-bind="field"
            label="Mobile number"
            label-type="floating"
            minlength="10"
            :maxlength="values.mobile?.startsWith('+') ? 15 : 10"
            @keypress="onlyValidPhoneKeys"
          />
        </BxField>
        <BxField v-slot="{ field }" name="email">
          <BxInput
            :id="values.id + '-email'"
            :data-test-id="values.id + '-email'"
            class="form-control"
            inputmode="email"
            v-bind="field"
            label="Email address"
            label-type="floating"
            @keypress="noWhiteSpaceKeys"
          />
        </BxField>
        <BxField v-slot="{ field }" name="confirmEmail">
          <BxInput
            :id="values.id + '-confirm-email'"
            :data-test-id="values.id + '-confirm-email'"
            class="form-control"
            inputmode="email"
            v-bind="field"
            label="Confirm email address"
            label-type="floating"
            @keypress="noWhiteSpaceKeys"
          />
        </BxField>
        <BxErrorPanel v-show="!meta.valid && submitCount > 0" class="mt-6">
          Please review the error messages above.
        </BxErrorPanel>
      </template>
    </div>
  </form>
</template>

<style lang="scss" scoped>
.individual-note {
  background: #f5f9fc;
  width: fit-content;
  padding: 0.6rem;
  border-radius: 0.3rem;
  margin: 1rem 0;

  strong,
  p {
    font-size: 1rem;
    line-height: 1.3rem;
  }

  strong {
    color: #040a34;
  }

  p {
    font-weight: 500;
    color: #02864e;
    font-size: 0.8rem;
    margin-top: 0.2rem;
  }
}
</style>
